<template>
  <div class="employee-selector">
    <div class="employee-selector__trigger" @click="toggleDropdown">
      <span
        v-if="showPlaceholder"
        class="employee-selector__trigger--full"
        key="placeholder"
      >
        {{ placeholder }}
      </span>
      <span
        v-for="(employee, index) of Object.values(selected)"
        :key="index"
        class="employee-selector__trigger--full"
      >
        {{ employee.name }}
      </span>
      <span
        class="employee-selector__trigger--clear"
        v-if="hasSelection"
        @click.stop="clearSelection"
      >
        <b-icon icon="times" class="icon"></b-icon>
      </span>
    </div>
    <div
      :class="[
        'employee-selector__dropdown',
        open && 'employee-selector__dropdown--visible',
      ]"
      v-on-clickaway="closeDropdown"
    >
      <input
        type="search"
        class="input employee-selector__dropdown__input"
        ref="filterInput"
        v-model="filterInput"
        placeholder="Filtrér..."
        @keydown.enter="select"
        @keydown.esc="closeDropdown"
        @keydown.down="navigateNext"
        @keydown.up="navigatePrev"
      />
      <div class="employee-selector__dropdown__list-container">
        <div class="employee-selector__dropdown__list">
          <div
            v-for="(group, gindex) in filteredEmployees"
            :key="gindex"
            class="employee-selector__dropdown__group"
          >
            <span>
              {{ group.group ? group.group.title : "-" }}
            </span>
            <div class="employee-selector__dropdown__group--members">
              <div
                v-for="(employee, eindex) in group.employees"
                :key="eindex"
                :class="[
                  'employee-selector__dropdown__group__member',
                  cursorOnItem(gindex, eindex) &&
                    'employee-selector__dropdown__group--cursor',
                ]"
                @click="navigateClick(gindex, eindex)"
              >
                <span>{{ employee.number }}</span>
                <span class="dropdown__column--name">{{ employee.name }}</span>
                <span>{{ employee.role[0] }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { mixin as clickaway } from "vue-clickaway";
import _ from "lodash";

export default {
  name: "employee-selector",
  mixins: [clickaway],
  props: {
    multiple: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
    currentUser: {
      type: Object,
      default: null,
    },
    showAll: {
      type: Boolean,
      default: false,
    },
    employees: {
      type: Array,
      default: undefined,
    },
  },
  beforeDestroy() {
    this.clearSelection();
  },
  data: () => ({
    closable: false,
    cursor: { gindex: 0, eindex: 0 },
    filterInput: "",
    open: false,
    selected: {},
  }),
  async mounted() {
    await this.getAllGroups();
    if (this.employees === undefined) {
      await this.getAllEmployees();
    } else {
      await this.setAllEmployees(this.employees);
    }

    if (this.multiple) {
      alert("Multiple selection not implemented.");
    }
  },
  methods: {
    ...mapActions("groupStore", ["getAllGroups", "getGroupById"]),
    ...mapActions("employees", ["getAllEmployees", "setAllEmployees"]),
    cursorOnItem(gindex, eindex) {
      return this.cursor.gindex === gindex && this.cursor.eindex === eindex;
    },
    navigateClick(gindex, eindex) {
      //if (this.cursor.gindex === gindex && this.cursor.eindex === eindex) {
      //  this.select();
      //}
      this.cursor.gindex = gindex;
      this.cursor.eindex = eindex;
      this.select();
      this.$refs.filterInput.focus();
    },
    navigatePrev() {
      if (this.cursor.eindex > 0) {
        this.cursor.eindex--;
      } else if (this.cursor.gindex > 0) {
        this.cursor.gindex--;
        this.cursor.eindex =
          this.filteredEmployees[this.cursor.gindex].employees.length - 1;
      } else {
        this.cursor.gindex = this.filteredEmployees.length - 1;
        this.cursor.eindex =
          this.filteredEmployees[this.cursor.gindex].employees.length - 1;
      }
      this.$nextTick(() => {
        document
          .getElementsByClassName(
            "employee-selector__dropdown__group--cursor"
          )[0]
          .scrollIntoView(false);
      });
    },
    navigateNext() {
      if (
        this.cursor.eindex <
        this.filteredEmployees[this.cursor.gindex].employees.length - 1
      ) {
        this.cursor.eindex++;
      } else if (this.cursor.gindex < this.filteredEmployees.length - 1) {
        this.cursor.gindex++;
        this.cursor.eindex = 0;
      } else {
        this.cursor.gindex = 0;
        this.cursor.eindex = 0;
      }
      this.$nextTick(() => {
        document
          .getElementsByClassName(
            "employee-selector__dropdown__group--cursor"
          )[0]
          .scrollIntoView(false);
      });
    },
    closeDropdown() {
      if (this.closable) {
        this.open = false;
      }
    },
    toggleDropdown() {
      this.open = !this.open;
      if (this.open) {
        this.$nextTick(() => {
          this.$refs.filterInput.focus();
        });
        this.closable = false;
        _.debounce(() => {
          this.closable = true;
        }, 250)();
      }
    },
    select() {
      const group = this.filteredEmployees[this.cursor.gindex].group;
      const employee = this.filteredEmployees[this.cursor.gindex].employees[
        this.cursor.eindex
      ];

      if (!Object.prototype.hasOwnProperty.call(this.selected, employee.id)) {
        if (!this.multiple) {
          this.$set(this, "selected", {});
          this.closeDropdown();
        }
        this.$set(this.selected, employee.id, employee);
        this.$emit("selection-changed", { employee, group });
      } else {
        this.$delete(this.selected, employee.id);
        this.$emit("selection-changed", {
          employee: undefined,
          group: undefined,
        });
      }
    },
    clearSelection() {
      this.$set(this, "selected", {});
      this.$emit("selection-changed", {
        employee: undefined,
        group: undefined,
      });
    },
  },
  computed: {
    ...mapGetters("groupStore", ["groups"]),
    ...mapGetters("employees", [
      "allEmployeeApiState",
      "allEmployees",
      "allEmployeesLookup",
    ]),
    filteredEmployees() {
      const filterLowercase = this.filterInput.toLowerCase();
      const currentGroups = this.groups;
      const processEmployee = (employee, groupId, groupName, dictionary) => {
        if(!currentGroups.find((o) => o.id === groupId)) {
          currentGroups.push({
            id: groupId,
            title: groupName,
          });
        }

        if (!Object.prototype.hasOwnProperty.call(dictionary, employee.id)) {
          this.$set(dictionary, employee.id, {
            employeeId: employee.id,
            employee: employee,
            groups: [{ id: groupId, groupName: groupName }],
          });
        }
        
        const current = dictionary[employee.id];
        if (!current.groups.find((o) => o.id === groupId)) {
          dictionary[employee.id].groups.push({
            id: groupId,
            groupName: groupName,
          });
        }
      };

      // Process records into normalized format in a single dataset
      const relations = {}; // employeeId, employee, groups[]
      this.groups.forEach((group) => {
        group.employees?.forEach((employee) => {
          let lookedUpEmployee = this.allEmployeesLookup(employee.employeeId);
          if (lookedUpEmployee) {
            processEmployee(lookedUpEmployee, group.id, group.title, relations);
          }
        });
      });

      if (!this.currentUser || this.showAll) {
        this.allEmployees
          .filter((q) => q.role !== "NA" && q.isActive)
          .forEach((employee) => {
            processEmployee(
              employee,
              employee.groupId,
              employee.groupName,
              relations
            );
          });
      } else {
        for (const employee of this.allEmployees.filter(
          (q) => q.role !== "NA" && q.isActive
        )) {
          const group = this.groups.find((o) => o.id === employee.groupId);
          if (group) {
            processEmployee(
              employee,
              employee.groupId,
              employee.groupName,
              relations
            );
          }
        }
      }

      // Filter records and funnel employees to groups
      const addToGroup = (employee, group, dictionary) => {
        if (!Object.prototype.hasOwnProperty.call(dictionary, group)) {
          this.$set(dictionary, group, []);
        }
        dictionary[group].push(employee);
      };
      const grouped = {};
      Object.values(relations).forEach((item) => {
        // employee matched - add instance to all related groups.
        if (item.employee.name?.toLowerCase().includes(filterLowercase) || `${item.employee.number}`?.toLowerCase().includes(filterLowercase) || `${item.employee?.groupName}`?.toLowerCase().includes(filterLowercase)) {
          item.groups.forEach((group) =>
            addToGroup(item.employee, group.id, grouped)
          );
        } else {
          // add employee to any matched related group
          item.groups
            .filter((group) => group.groupName?.includes(filterLowercase))
            .forEach((group) => {
              addToGroup(item.employee, group.id, grouped);
            });
        }
      });
      this;

      const output = [];
      const employeeNameSort = (a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      };
      for (let groupId of Object.keys(grouped)) {
        let group = currentGroups.find((o) => o.id === groupId);

        output.push({
          groupId: groupId,
          group: group,
          employees: grouped[groupId].sort(employeeNameSort),
        });
      }
      
      return output;
    },
    showPlaceholder() {
      return this.placeholder !== "" && !this.hasSelection;
    },
    hasSelection() {
      return Object.keys(this.selected).length > 0;
    },
  },
  watch: {
    employees(newValue) {
      if (newValue !== undefined) {
        this.setAllEmployees(newValue);
      }
    },
    filteredEmployees() {
      this.cursor.gindex = 0;
      this.cursor.eindex = 0;
    },
  },
};
</script>

<style lang="scss">
.employee-selector {
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  max-width: 768px;
  position: relative;

  .icon {
    height: 24px;
  }
  .employee-selector__trigger {
    flex: 1 1 30px;
    background-color: $secondary;
    color: white;
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
    padding: 0 5px;
    .employee-selector__trigger--full {
      flex: 1 1 auto;
      text-align: left;
    }
    .employee-selector__trigger--clear {
      flex: 0 0 24px;
      cursor: pointer;
    }
  }
  .employee-selector__dropdown {
    position: absolute;
    flex: 1 1 auto;
    visibility: hidden;
    transform-origin: top;
    width: 100%;
    z-index: 5;
    background-color: $secondary;
    top: 30px;
    padding: 3px 3px;

    &.employee-selector__dropdown--visible {
      visibility: visible;
      -webkit-animation: blind-in 0.25s 1;
      animation: blind-in 0.25s 1;
    }

    .employee-selector__dropdown__input {
    }
    .employee-selector__dropdown__list-container {
      overflow-y: auto;
      max-height: 40vh;

      /* Prvent text selection */
      -youbkit-touch-callout: none; /* iOS Safari */
      -youbkit-user-select: none; /* Chrome 6.0+, Safari 3.1+, Edge & Opera 15+ */
      -moz-user-select: none; /* Firefox */
      -ms-user-select: none; /* IE 10+ and Edge */
      user-select: none; /* Current Chrome, Opera */

      .employee-selector__dropdown__list {
        display: flex;
        flex-flow: column nowrap;
        gap: 5px;

        .employee-selector__dropdown__group {
          background-color: $panel-row;
          color: $panel;

          .employee-selector__dropdown__group__member {
            display: flex;
            flex-flow: row nowrap;
            gap: 10px;
            color: $panel-row;

            span:first-child {
              width: 45px;
              text-align: right;
            }
            span:not(first-child) {
              border-left: 2px solid $secondary;
              padding-left: 5px;
            }
            span.dropdown__column--name {
              flex: 1 0 auto;
              text-overflow: ellipsis;
              white-space: nowrap;
              overflow: hidden;
              text-align: left;
              max-width: calc(100% - 90px);
              color: $black;
            }

            &:hover {
              background-color: $border;
            }
          }

          .employee-selector__dropdown__group--members {
            background-color: #c9c8c8;
            color: black;
          }
          .employee-selector__dropdown__group--cursor {
            background-color: ivory;
            &:hover {
              background-color: ivory;
            }
          }
        }
      }
    }
  }
}

@-webkit-keyframes blind-in {
  0% {
    transform: scaleY(0);
  }
  100% {
    transform: scaleY(1);
  }
}
@keyframes blind-in {
  0% {
    transform: scaleY(0);
  }
  100% {
    transform: scaleY(1);
  }
}
</style>
